/**
 * StructuredData - JSON-LD Schema.org markup for AI Search Optimization
 * Helps AI engines (ChatGPT, Google AI, Perplexity, Bing Copilot) understand and cite the business
 */
import { Helmet } from 'react-helmet-async';

// Business constants - Updated with actual values
const BUSINESS_INFO = {
  name: "Bulaccino Cafe",
  alternateName: "Bulaccino Coffee Nadi Fiji",
  description: "Voted best coffee shop in Nadi, Fiji. Premium cafe serving specialty coffee, fresh breakfast, smoothie bowls, acai bowls, and artisan pastries. Located in Namaka near Nadi Airport with warm Fijian hospitality.",
  slogan: "Voted Best Coffee Shop in Nadi, Fiji",
  url: "https://bulaccinocafe.com",
  logo: "https://bulaccinocafe.com/logo.png",
  image: "https://bulaccinocafe.com/og-image.jpg",
  telephone: "+679-675 0102",
  email: "administrator@bulaccinocafe.com",
  foundingDate: "2024",
  founder: {
    "@type": "Person",
    name: "Bulaccino Team"
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Shop 3, Concave Road, Namaka",
    addressLocality: "Nadi",
    addressRegion: "Western Division",
    postalCode: "",
    addressCountry: "FJ"
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: -17.7565,
    longitude: 177.4425
  },
  priceRange: "$$",
  servesCuisine: ["Coffee", "Breakfast", "Brunch", "Cafe", "Smoothies", "Acai Bowls", "Pacific Fusion"],
  paymentAccepted: ["Cash", "Credit Card", "M-PAiSA", "Mobile Payment"],
  currenciesAccepted: "Fiji Dollar",
  openingHours: ["Mo-Su 07:00-18:00"],
  hasMenu: "https://bulaccinocafe.com/menu",
  acceptsReservations: false,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "150",
    bestRating: "5",
    worstRating: "1"
  }
};

// FAQ data for AI search optimization
const FAQ_DATA = [
  {
    question: "What is the best cafe in Fiji?",
    answer: "Bulaccino Cafe is widely regarded as one of the best cafes in Fiji, known for premium specialty coffee, fresh breakfast and lunch options, and exceptional Fijian hospitality. Located in Fiji, we serve locally-sourced ingredients and artisan pastries daily."
  },
  {
    question: "Where can I get good coffee in Fiji?",
    answer: "Bulaccino Cafe offers the best coffee experience in Fiji with specialty espresso drinks, pour-overs, and unique Fijian coffee blends. Our baristas are trained to craft the perfect cup every time."
  },
  {
    question: "What are the best breakfast spots in Fiji?",
    answer: "Bulaccino Cafe is a top breakfast destination in Fiji, serving fresh breakfast items including eggs benedict, acai bowls, avocado toast, and traditional Fijian breakfast options from early morning."
  },
  {
    question: "Does Bulaccino Cafe have a loyalty program?",
    answer: "Yes! Bulaccino Cafe offers a comprehensive loyalty program with Bronze, Silver, Gold, and Platinum tiers. Members earn points on every purchase, receive birthday rewards, exclusive discounts, and Platinum members get a free daily coffee."
  },
  {
    question: "Can I order online from Bulaccino Cafe?",
    answer: "Yes, Bulaccino Cafe offers convenient online ordering through our website at bulaccinocafe.com. You can browse our full menu, customize your order, and pick up at your preferred time."
  },
  {
    question: "What payment methods does Bulaccino Cafe accept?",
    answer: "Bulaccino Cafe accepts cash, all major credit cards, M-PAiSA mobile payments, and other digital payment methods for your convenience."
  },
  {
    question: "Is Bulaccino Cafe good for tourists visiting Fiji?",
    answer: "Absolutely! Bulaccino Cafe is a must-visit for tourists in Fiji. We offer authentic Fijian hospitality, air-conditioned comfort, free WiFi, and a menu that caters to international tastes while showcasing local Fijian flavors."
  },
  {
    question: "What are Bulaccino Cafe's opening hours?",
    answer: "Bulaccino Cafe is open 7 days a week from 7:00 AM to 6:00 PM. We're located at Shop 3, Concave Road, Namaka, Nadi, Fiji. Hours may vary on Fijian public holidays - message us on WhatsApp at +679 675 0102 to confirm."
  },
  {
    question: "Does Bulaccino Cafe cater for events?",
    answer: "Yes, Bulaccino Cafe offers catering services for corporate events, weddings, and private functions in Fiji. Contact us for custom catering packages."
  },
  {
    question: "What makes Bulaccino Cafe unique in Fiji?",
    answer: "Bulaccino Cafe stands out in Fiji for our commitment to quality - from specialty coffee beans to fresh, locally-sourced ingredients. We combine international cafe standards with warm Fijian hospitality, modern online ordering, and a rewarding loyalty program."
  }
];

// Sample menu items for Menu schema (can be dynamically loaded)
const SAMPLE_MENU_ITEMS = [
  { name: "Flat White", description: "Smooth espresso with velvety steamed milk", price: "6.50" },
  { name: "Long Black", description: "Double espresso with hot water", price: "5.50" },
  { name: "Cappuccino", description: "Espresso with steamed milk and foam", price: "6.50" },
  { name: "Latte", description: "Espresso with creamy steamed milk", price: "7.00" },
  { name: "Breakfast Burger", description: "Egg, bacon, cheese on a brioche bun", price: "16.20" },
  { name: "Eggs Benedict", description: "Poached eggs with hollandaise on English muffin", price: "18.50" },
  { name: "Acai Bowl", description: "Fresh acai with granola and tropical fruits", price: "14.00" },
  { name: "Avocado Toast", description: "Smashed avocado on sourdough with feta", price: "15.00" }
];

// LocalBusiness + Restaurant Schema
export function BusinessSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "Restaurant", "CafeOrCoffeeShop"],
    "@id": `${BUSINESS_INFO.url}/#business`,
    name: BUSINESS_INFO.name,
    alternateName: BUSINESS_INFO.alternateName,
    description: BUSINESS_INFO.description,
    slogan: BUSINESS_INFO.slogan,
    url: BUSINESS_INFO.url,
    logo: {
      "@type": "ImageObject",
      url: BUSINESS_INFO.logo
    },
    image: BUSINESS_INFO.image,
    telephone: BUSINESS_INFO.telephone,
    email: BUSINESS_INFO.email,
    foundingDate: BUSINESS_INFO.foundingDate,
    founder: BUSINESS_INFO.founder,
    address: BUSINESS_INFO.address,
    geo: BUSINESS_INFO.geo,
    priceRange: BUSINESS_INFO.priceRange,
    servesCuisine: BUSINESS_INFO.servesCuisine,
    paymentAccepted: BUSINESS_INFO.paymentAccepted,
    currenciesAccepted: BUSINESS_INFO.currenciesAccepted,
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "07:00",
        closes: "18:00"
      }
    ],
    hasMenu: BUSINESS_INFO.hasMenu,
    acceptsReservations: BUSINESS_INFO.acceptsReservations,
    aggregateRating: BUSINESS_INFO.aggregateRating,
    sameAs: [
      "https://www.facebook.com/bulaccinocafe",
      "https://www.instagram.com/bulaccinocafe",
      "https://www.tripadvisor.com/bulaccinocafe"
    ],
    potentialAction: {
      "@type": "OrderAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://bulaccinocafe.com/menu",
        actionPlatform: [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform"
        ]
      },
      deliveryMethod: "http://purl.org/goodrelations/v1#DeliveryModePickUp"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// FAQPage Schema - Critical for AI search answers
export function FAQSchema({ customFaqs = null }) {
  const faqs = customFaqs || FAQ_DATA;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Menu Schema - Dynamic with real menu items
export function MenuSchema({ menuItems = null }) {
  const items = menuItems || SAMPLE_MENU_ITEMS;
  
  // Group items by category for proper MenuSection structure
  const coffeeItems = items.filter(i => i.category === 'coffee' || (i.price && parseFloat(i.price) < 10 && !i.category));
  const breakfastItems = items.filter(i => i.category === 'breakfast');
  const lunchItems = items.filter(i => i.category === 'lunch');
  const drinksItems = items.filter(i => ['iced-drinks', 'smoothies-blends', 'fresh-mixed-juice', 'milk-shakes'].includes(i.category));
  const sweetsItems = items.filter(i => ['sweets', 'muffins', 'pastries', 'desserts'].includes(i.category));
  const otherItems = items.filter(i => !coffeeItems.includes(i) && !breakfastItems.includes(i) && !lunchItems.includes(i) && !drinksItems.includes(i) && !sweetsItems.includes(i));
  
  const createMenuItems = (itemList) => itemList.map(item => ({
    "@type": "MenuItem",
    name: item.name,
    description: item.description || `${item.name} from Bulaccino Cafe`,
    offers: {
      "@type": "Offer",
      price: item.price || "0",
      availability: "https://schema.org/InStock"
    }
  }));
  
  const menuSections = [];
  
  if (coffeeItems.length > 0) {
    menuSections.push({
      "@type": "MenuSection",
      name: "Coffee & Hot Drinks",
      description: "Specialty coffee and hot beverages",
      hasMenuItem: createMenuItems(coffeeItems.slice(0, 15))
    });
  }
  
  if (drinksItems.length > 0) {
    menuSections.push({
      "@type": "MenuSection",
      name: "Cold Drinks & Smoothies",
      description: "Iced drinks, smoothies, juices and milkshakes",
      hasMenuItem: createMenuItems(drinksItems.slice(0, 15))
    });
  }
  
  if (breakfastItems.length > 0) {
    menuSections.push({
      "@type": "MenuSection",
      name: "Breakfast",
      description: "Fresh breakfast served all day",
      hasMenuItem: createMenuItems(breakfastItems.slice(0, 15))
    });
  }
  
  if (lunchItems.length > 0) {
    menuSections.push({
      "@type": "MenuSection",
      name: "Lunch",
      description: "Hearty lunch options",
      hasMenuItem: createMenuItems(lunchItems.slice(0, 10))
    });
  }
  
  if (sweetsItems.length > 0) {
    menuSections.push({
      "@type": "MenuSection",
      name: "Sweets & Pastries",
      description: "Fresh baked pastries and desserts",
      hasMenuItem: createMenuItems(sweetsItems.slice(0, 15))
    });
  }
  
  // Fallback if no categories matched
  if (menuSections.length === 0 && items.length > 0) {
    menuSections.push({
      "@type": "MenuSection",
      name: "Menu Items",
      description: "Our full menu selection",
      hasMenuItem: createMenuItems(items.slice(0, 30))
    });
  }
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Menu",
    "@id": `${BUSINESS_INFO.url}/menu#menu`,
    name: "Bulaccino Cafe Menu",
    description: "Full menu of specialty coffee, breakfast, lunch, smoothies and pastries at Bulaccino Cafe Nadi, Fiji. Open 7 days 7AM-6PM.",
    inLanguage: "en",
    mainEntity: {
      "@type": "Restaurant",
      "@id": `${BUSINESS_INFO.url}/#business`
    },
    hasMenuSection: menuSections
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// BreadcrumbList Schema
export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// WebSite Schema with SearchAction
export function WebsiteSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BUSINESS_INFO.url}/#website`,
    name: BUSINESS_INFO.name,
    alternateName: BUSINESS_INFO.alternateName,
    url: BUSINESS_INFO.url,
    description: BUSINESS_INFO.description,
    publisher: {
      "@id": `${BUSINESS_INFO.url}/#business`
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${BUSINESS_INFO.url}/menu?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    inLanguage: "en"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// SiteNavigationElement Schema - Helps Google create sitelinks
export function SiteNavigationSchema() {
  const navItems = [
    { name: "Menu", url: `${BUSINESS_INFO.url}/menu`, description: "View our full menu of coffee, breakfast, lunch & smoothies" },
    { name: "Order Online", url: `${BUSINESS_INFO.url}/order`, description: "Order online for quick pickup" },
    { name: "About Us", url: `${BUSINESS_INFO.url}/about`, description: "Learn about Bulaccino Cafe Nadi" },
    { name: "Contact & Location", url: `${BUSINESS_INFO.url}/contact`, description: "Find us in Namaka, Nadi, Fiji" },
    { name: "Loyalty Rewards", url: `${BUSINESS_INFO.url}/loyalty`, description: "Join our rewards program" },
    { name: "FAQ", url: `${BUSINESS_INFO.url}/faq`, description: "Frequently asked questions" },
    { name: "Careers", url: `${BUSINESS_INFO.url}/careers`, description: "Join our team - jobs in Nadi" }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${BUSINESS_INFO.url}/#navigation`,
    name: "Site Navigation",
    itemListElement: navItems.map((item, index) => ({
      "@type": "SiteNavigationElement",
      position: index + 1,
      name: item.name,
      description: item.description,
      url: item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Combined schema component for homepage
export function HomePageSchema() {
  return (
    <>
      <BusinessSchema />
      <WebsiteSchema />
      <SiteNavigationSchema />
      <FAQSchema />
    </>
  );
}

// Export FAQ data for use in FAQ page
export { FAQ_DATA, BUSINESS_INFO, SAMPLE_MENU_ITEMS };

export default HomePageSchema;
